// Zod schema for the WHOLE docs/balance/baseline.yaml. Objects are strict, so an unknown or
// misspelt key fails validation instead of silently falling back to a default.
import { z } from 'zod';
import { SHIP_TYPES } from './enums.js';

const LEVELS = 21; // index 0 = level 0 … index 20 = level 20

const int = z.number().int().nonnegative();
const pct = int;
const positive = z.number().positive();
const range = z.tuple([int, int]);
const levelTable = <T extends z.ZodType>(item: T) => z.array(item).length(LEVELS);

const shipType = z.enum(SHIP_TYPES);
const shipCounts = z.partialRecord(shipType, int);
const shipRanges = z.partialRecord(shipType, range);

const resourceCost = z.strictObject({
  metal: int.optional(),
  gas: int.optional(),
  supplies: int.optional(),
});
const resources = z.strictObject({ metal: int, gas: int, supplies: int });

const building = z.strictObject({ cost: resourceCost, time_weight: positive });

const galaxy = z.strictObject({
  seed_core_sectors: int,
  systems_per_sector: range,
  prototype: z.strictObject({ sectors: int, target_players: int }),
  speed_pct: z.strictObject({ live: int, test_max: int }),
  wormhole_pairs_per_sectors: int,
  expansion_trigger_unclaimed_frontier_systems: int,
  spawn_cluster_players: range,
  spawn_cluster_fill_h: range,
  min_system_spacing_ly: positive,
  typical_neighbour_distance_ly: positive,
  max_gate_link_distance_ly: positive,
  system_type_frequency_pct: z.strictObject({
    standard: pct,
    resource_rich: pct,
    strategic: pct,
    barren: pct,
  }),
  planets: z.strictObject({
    standard: z.strictObject({ each_type: range }),
    strategic: z.strictObject({ each_type: range }),
    resource_rich: z.strictObject({ dominant_type: range, other_types: range }),
    barren: z.strictObject({ each_type: range, total: range }),
  }),
  planet_bonus: z.strictObject({ base_pct: pct, per_planet_pct: pct, max_pct: pct }),
  galaxy_peace_threshold_pct: pct,
  unowned_system_building_level: int,
});

const travel = z.strictObject({
  direct_travel_multiplier_pct: pct,
  warp_gate_multiplier: z.strictObject({
    base_pct: pct,
    per_level_pct: pct,
    leg_rule: z.literal('departure_system_gate'),
    own_gate_usable_at_any_level: z.boolean(),
  }),
  wormhole_travel_s: int,
  recall: z.literal('mirror_elapsed_time'),
  timed_dispatch_max_ahead_h: positive,
  timed_orders_max_open: int,
});

const levels = z.strictObject({
  max_level_normal: int,
  max_level_citadel: int,
  build_queue_length: int,
  cost_multiplier: levelTable(positive.nullable()),
  build_time_min: levelTable(positive.nullable()),
  capture_queue_refund_pct: pct,
  cancel_refund_pct: z.strictObject({
    building: pct,
    research: pct,
    ship_production_undelivered: pct,
    port_trade: pct.nullable(),
  }),
});

const buildings = z.strictObject({
  command_center: building,
  metal_mine: building,
  gas_harvester: building,
  supply_farm: building,
  metal_storage: building,
  gas_storage: building,
  supply_storage: building,
  trading_port: building,
  space_dock: building,
  habitat: building,
  orbital_defense_platform: building,
  warp_gate: building.extend({ artifacts: levelTable(int.nullable()) }),
  observatory: building,
  space_guild: building,
  research_center: building,
  diplomatic_embassy: building,
});

const economy = z.strictObject({
  production_per_h: levelTable(int),
  storage_cap: levelTable(int),
  citadel_multiplier_pct: pct,
  citadel_multiplier_applies_to: z.array(z.string()),
  trading_port: z.strictObject({
    credits_per_1000: int,
    trade_duration_h: positive,
    concurrent_trades_per_port: int,
    min_trade_resources: int,
    max_resources_per_trade: levelTable(int),
  }),
  claims: z.strictObject({
    base_cost_credits: int,
    citadel_counts_as_systems: int,
    pending_h: positive,
    expires_after_placement_h: positive,
    refund_pct: z.strictObject({
      cancel_pending: pct,
      cancel_active: pct,
      expired: pct,
      invalidated: pct,
    }),
  }),
  citadel: z.strictObject({
    base_cost_credits: int,
    systems_merged: int,
    required_building_level: int,
  }),
  raiding: z.strictObject({
    protected_pct_of_storage_cap: pct,
    raid_throughput_cap_per_h: int.nullable(),
  }),
  unowned_systems: z.strictObject({
    stockpile_cap: int,
    regen_per_h: int,
    stockpile_multiplier_pct: z.strictObject({
      no_pirates: pct,
      pirates: pct,
      pirate_outpost: pct,
    }),
    protected_pct: pct,
    stockpile_on_capture: z.string(),
  }),
  player_trade: z.strictObject({
    unlock_account_age_d: positive,
    shuttle_travel_min: positive,
    offers_per_h: int,
  }),
});

const influence = z.strictObject({
  production_per_h: levelTable(int),
  boost_cost_resources_per_production_unit: int,
  boost_cost_split: z.string(),
  boost_production_pct: pct,
  boost_duration_h: positive,
  boost_while_active: z.string(),
  storage_cap_hours_of_production: positive,
});

const scouting = z.strictObject({
  min_carried_influence: int,
  scout_speed_lyph: positive,
  success_rule: z.string(),
  unowned_systems_store_influence: z.boolean(),
  repeat_scout_cache_min: positive,
  scouts_per_player_per_h: int,
});

const openRange = z.tuple([int, int.nullable()]);
const observatory = z.strictObject({
  range_ly: levelTable(int),
  detail_from_level: z.strictObject({
    time_only: int,
    size_class: int,
    approximate: int,
    exact: int,
  }),
  size_classes_ships: z.strictObject({
    small: openRange,
    medium: openRange,
    large: openRange,
    huge: openRange,
  }),
  approximate_rounding: z.string(),
});

const tech = z.strictObject({
  tier: int,
  cost_each: int,
  time_min: positive,
  requires: z.array(z.string()),
  effect: z.record(z.string(), z.union([z.number(), z.string()])).nullable(),
  scope: z.string().optional(),
});

const research = z.strictObject({
  concurrent_per_system: int,
  tier_unlock_center_level: z.record(z.string(), int),
  techs: z.record(z.string(), tech),
});

const ship = z.strictObject({
  line: z.enum(['front', 'mid', 'back', 'none']),
  attack: int,
  defense: int,
  hull: z.number().int().positive(),
  speed_lyph: positive,
  cargo: int,
  cost: resourceCost.extend({ artifacts: int.optional() }),
  build_time_s: positive,
  dock_level: int,
  habitat_points: int,
});

// Shape keys in SHIP_TYPES order, so Object.keys(balance.ships) is the combat roll order.
const ships = z.strictObject({
  interceptor: ship,
  transport: ship,
  frigate: ship,
  cruiser: ship,
  carrier: ship,
  battleship: ship,
  dreadnought: ship,
});

const combat = z.strictObject({
  round_interval_s: z.strictObject({ default: int, min: int, max: int }),
  variance_max_pct: pct,
  damage_pct: pct,
  defense_constant: int,
  targeting_weight_pct: z.strictObject({ bonus: pct, neutral: pct, poor: pct }),
  matchup_pct: z.partialRecord(shipType, shipCounts),
  escort_screen: z.strictObject({ per_frigate_pct: pct, max_pct: pct }),
  carrier_wing: z.strictObject({
    interceptors_per_carrier: int,
    attack_pct: pct,
    defense_pct: pct,
  }),
  odp: z.strictObject({ attack: levelTable(int) }),
});

const pirateSpawn = z.strictObject({ spawn_chance_pct: pct, fleet: shipRanges });
const pirates = z.strictObject({
  spawn_check_h: positive,
  max_fleets_per_system: int,
  stays_until_destroyed: z.boolean(),
  heals_losses: z.boolean(),
  by_system_type: z.strictObject({
    barren: pirateSpawn,
    standard: pirateSpawn,
    strategic: pirateSpawn,
    resource_rich: pirateSpawn,
  }),
  outposts: z.strictObject({ per_seed_sector: int, respawn_h: positive, fleet: shipRanges }),
});

const missions = z.strictObject({
  concurrent_by_guild_level: z.record(z.string(), int),
  reward_carried_by_transports: z.boolean(),
  artifacts_per_success: int,
  tiers: z.array(
    z.strictObject({
      tier: int,
      guild_level: int,
      ships: shipCounts,
      duration_h: positive,
      loss_chance_pct: pct,
      reward_each: int,
      artifact_chance_pct: pct,
    })
  ),
});

const player = z.strictObject({
  beginner_shield_h: positive,
  shield_warnings_h: z.array(positive),
  starting_resources: resources,
  starting_buildings: z.record(z.string(), int),
  starter_fleet: shipCounts,
  tutorial_fleet: shipCounts,
  tutorial_rewards: z.record(z.string(), resourceCost),
  starting_missions: z.strictObject({ reward_credits: int, earn_credits_target: int.min(1) }),
  refugee: z.strictObject({ cooldown_d: positive, resources }),
});

const alliance = z.strictObject({
  max_members: int,
  max_members_pre_alpha: int.min(1),
  rename_cooldown_h: positive,
  leave_rejoin_cooldown_h: positive,
  kick_reapply_cooldown_h: positive,
  recruit_auto_promote_h: positive,
  invite_expiry_h: positive,
  overlord_inactive_transfer_d: positive,
  leaderless_disband_d: positive,
  broadcast_per_24h: int,
  chat_retention_d: positive,
  proposal_expiry_h: positive,
  downgrade_notice_h: positive,
  nap_breaker: z.strictObject({ mark_d: positive, doubled_proposal_expiry_d: positive }),
  war_declaration_warning_h: positive,
  surrender_war_cooldown_d: positive,
  ceasefire_nap_d: positive,
  war_score: z.strictObject({
    battle_won: range,
    battle_won_points_per_10000_resources_destroyed: int,
    capture: int,
    defend: int,
    raid_resources_per_point: int,
    fleet_destroyed: int,
  }),
});

const antiBot = z.strictObject({
  attack_challenge_ttl_s: positive,
  challenge_pass_min: positive,
  challenge_retry_delay_s: positive,
});

const speedUps = z.strictObject({ dark_matter_per_hour: positive });
const darkMatter = z.strictObject({ tester_grant: int });
const inbox = z.strictObject({ economy_read_retention_days: positive });

export const balanceSchema = z.strictObject({
  meta: z.strictObject({ version: z.string(), updated: z.string() }),
  galaxy,
  travel,
  levels,
  buildings,
  economy,
  influence,
  scouting,
  observatory,
  research,
  ships,
  space_dock: z.strictObject({ build_time_pct: levelTable(positive.nullable()) }),
  stealth: z.strictObject({ cost_pct: pct, eligible_classes: z.array(shipType).nullable() }),
  habitat: z.strictObject({
    production_capacity_points: levelTable(int),
    on_capture: z.string(),
    on_citadel_merge: z.string(),
  }),
  combat,
  pirates,
  missions,
  player,
  alliance,
  anti_bot: antiBot,
  speed_ups: speedUps,
  dark_matter: darkMatter,
  inbox,
});

export type Balance = z.infer<typeof balanceSchema>;
